import type { ReactElement } from "react";

import classNames from "classnames";
import { useState } from "react";
import { Navbar, Offcanvas } from "react-bootstrap";
import { useMatch } from "react-router-dom";

import Chip from "@/components/chip";
import { useOffcanvas } from "@/components/context/OffcanvasContext";
import MobileLanguageSwitcher from "@/components/languageSwitcher/MobileLanguageSwitcher";
import Logo from "@/components/logo";
import LogoLink from "@/components/logoShortLink";
import LogoutButton from "@/components/logoutButton";
import UserSwitchInfo from "@/components/sidebar/components/UserSwitchInfo";
import WebViewEntryInfo from "@/components/sidebar/components/WebViewEntryInfo";
import { IS_ME_LINK } from "@/constants/app";
import { ROUTES } from "@/constants/routes";
import { isWebView, webViewVersion } from "@/constants/webView";
import useLocalizedHref from "@/hooks/useLocalizedHref";
import { useAuthService } from "@/services/authService";

import styles from "./MobileNavbar.module.scss";

interface IMobileNavbarProps {
  menu: ReactElement;
}

function MobileNavbar({ menu }: IMobileNavbarProps) {
  const offcanvas = useOffcanvas();
  const localizedHref = useLocalizedHref();
  const { isSwitchedUser, username, fullName } = useAuthService();
  const isProfileRoute = useMatch(ROUTES.PROFILE);
  const isReferrerRoute = useMatch(ROUTES.REFERRER);
  const trimmedName = fullName.trim();
  const chipLabel = trimmedName
    ? trimmedName.split(/\s+/).length >= 2
      ? (
          trimmedName.split(/\s+/)[0][0] + trimmedName.split(/\s+/)[1][0]
        ).toUpperCase()
      : trimmedName.slice(0, 2).toUpperCase()
    : username.slice(0, 2).toUpperCase();

  const [showBurger, setShowBurger] = useState(
    !(
      isWebView &&
      (webViewVersion ?? 0) >= 3 &&
      (isProfileRoute || isReferrerRoute)
    )
  );

  return (
    <Navbar expand={false} className={classNames("d-lg-none", styles.nav)}>
      {showBurger && (
        <div
          className={classNames(
            "d-flex justify-content-start",
            styles.toggleWrapper
          )}
        >
          <Navbar.Toggle
            aria-controls={`offcanvasNavbar-expand-${true}`}
            className={classNames("py-0 px-0", styles.toggle)}
            onClick={() => offcanvas.open()}
          />
        </div>
      )}
      <Navbar.Offcanvas
        show={offcanvas.show}
        onHide={offcanvas.close}
        id={`offcanvasNavbar-expand-${false}`}
        aria-labelledby={`offcanvasNavbarLabel-expand-${false}`}
        placement="start"
        className={classNames({
          "bg-danger-subtle": isSwitchedUser,
        })}
      >
        <Offcanvas.Header closeButton className={styles.offcanvasHeader} />
        <Offcanvas.Body>
          <UserSwitchInfo />
          {menu}
          {!isWebView && (
            <>
              <hr />
              <div>
                <MobileLanguageSwitcher />
                <LogoutButton className="w-100 mt-1" />
              </div>
            </>
          )}
        </Offcanvas.Body>
      </Navbar.Offcanvas>
      {isWebView && location.pathname === localizedHref(ROUTES.ENTRY) ? (
        <WebViewEntryInfo setShowBurger={setShowBurger} />
      ) : (
        <>
          <div>{IS_ME_LINK ? <LogoLink /> : <Logo />}</div>
          <div
            className={classNames(
              "d-flex justify-content-end",
              styles.chipWrapper
            )}
          >
            <Chip label={chipLabel} />
          </div>
        </>
      )}
    </Navbar>
  );
}

export default MobileNavbar;
